import {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {useDNT} from '@salesforce/commerce-sdk-react';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {updateGTMConsent} from '@amblique/king-living/app/utils/gtm-consent';
import {getGtmConfigFromSite} from '@amblique/king-living/app/utils/gtm-utils';
import {
  applyPrivacySignal, DENIED_CONSENT, getConsentWithoutCookiebot,
  getCookiebotConfig, getCookiebotConsent, getDefaultDnt,
} from '@amblique/king-living/app/utils/tracking-consent';

const CONSENT_EVENTS = ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline', 'CookiebotOnLoad'];
const DNT_RETRY_DELAYS = [1000, 2000];

const CookiebotConsent = ({onDntUpdating, onDntSynchronized}) => {
  const {site, locale} = useMultiSite();
  const {effectiveDnt, updateDnt} = useDNT();
  const config = getCookiebotConfig(site, locale);
  const {enabled: gtmEnabled} = getGtmConfigFromSite(site);
  const [choice, setChoice] = useState(null);
  const queue = useRef(Promise.resolve());
  const updateDntRef = useRef(updateDnt);
  const onDntUpdatingRef = useRef(onDntUpdating);
  const onDntSynchronizedRef = useRef(onDntSynchronized);

  updateDntRef.current = updateDnt;
  onDntUpdatingRef.current = onDntUpdating;
  onDntSynchronizedRef.current = onDntSynchronized;

  useEffect(() => {
    setChoice(null);
    if (!config.enabled) return;
    if (!config.valid) {
      console.error('Cookiebot is enabled but its domain group ID is missing or invalid. Tracking remains denied.');

      return;
    }

    const readConsent = () => {
      const next = applyPrivacySignal(getCookiebotConsent(window.Cookiebot));

      window.__klCookiebotConsentReady = true;
      setChoice({siteId: site.id, consent: next});
    };

    CONSENT_EVENTS.forEach((event) => window.addEventListener(event, readConsent));
    const existing = document.getElementById('Cookiebot');

    if (existing && (existing.dataset.cbid !== config.domainGroupId || existing.dataset.culture !== config.culture)) {
      // Cookiebot has one global instance. A market/language change needs a fresh document.
      window.location.reload();
    } else if (!existing) {
      const script = document.createElement('script');

      script.id = 'Cookiebot';
      script.src = 'https://consent.cookiebot.com/uc.js';
      script.async = true;
      script.dataset.cbid = config.domainGroupId;
      script.dataset.culture = config.culture;
      script.dataset.blockingmode = 'none';
      // This bridge is the sole writer of Google consent updates.
      script.dataset.consentmode = 'disabled';
      script.onerror = () => console.error('Cookiebot could not load. Tracking remains denied.');
      document.head.appendChild(script);
    }
    if (window.__klCookiebotConsentReady || window.Cookiebot?.hasResponse) readConsent();

    return () => {
      CONSENT_EVENTS.forEach((event) => window.removeEventListener(event, readConsent));
    };
  }, [config.enabled, config.valid, config.domainGroupId, config.culture, site.id]);

  const ready = !config.enabled || (choice?.siteId === site.id && config.valid);
  const consent = applyPrivacySignal(config.enabled ?
    (ready ? choice.consent : DENIED_CONSENT) :
    getConsentWithoutCookiebot(site, effectiveDnt));
  // Statistics alone controls the SFCC DNT cookie and SLAS claim.
  // Legacy Google preferences must not change native SFCC tracking.
  const desiredDnt = config.enabled ? !consent.statistics : effectiveDnt !== false || getDefaultDnt(site);

  useEffect(() => {
    let cancelled = false;
    let retryTimer;

    const synchronize = (attempt = 0) => {
      // Serialize SLAS refreshes and skip obsolete choices before starting a request.
      queue.current = queue.current.catch(() => undefined).then(async () => {
        if (cancelled) return;
        // Reconcile both cookie and token, even if the cookie appears to match.
        const update = updateDntRef.current(desiredDnt);

        // The SDK writes/removes the cookie before awaiting SLAS. Refresh native
        // consumers now as well, so withdrawal does not retain an old granted render.
        onDntUpdatingRef.current?.();
        await update;
        // The SDK's token notification can precede its final DNT cookie write.
        // Refresh mounted consumers after completion without replacing native hooks.
        if (!cancelled) onDntSynchronizedRef.current?.();
      }).catch(() => {
        if (cancelled) return;
        if (attempt < DNT_RETRY_DELAYS.length) {
          retryTimer = setTimeout(() => synchronize(attempt + 1), DNT_RETRY_DELAYS[attempt]);
        } else {
          console.error('SFCC tracking consent could not be updated after three attempts.');
        }
      });
    };

    synchronize();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
    // A fresh Cookiebot event also retries an unchanged choice after a failure.
  }, [choice, desiredDnt, site.id]);

  useEffect(() => {
    if (gtmEnabled) updateGTMConsent(consent);
  }, [consent.statistics, consent.marketing, consent.preferences, gtmEnabled]);

  return null;
};

CookiebotConsent.propTypes = {onDntUpdating: PropTypes.func, onDntSynchronized: PropTypes.func};

export default CookiebotConsent;
